// SPDX-License-Identifier: AGPL-3.0-or-later

import {currentInstanceTarget} from '@app/features/platform/transport/InstanceHTTP';
import {makeAutoObservable, runInAction} from 'mobx';

const STORAGE_KEY = 'flx_known_custom_domains';

class CustomDomainStore {
	domains: Set<string> = new Set();
	domainVersion = 0;
	private isFetching = false;
	private hasFetched = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.loadFromStorage();
	}

	private loadFromStorage(): void {
		if (typeof window === 'undefined' || !window.localStorage) return;
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (raw) {
				const list = JSON.parse(raw);
				if (Array.isArray(list)) {
					for (const item of list) {
						if (typeof item === 'string' && item.trim()) {
							this.domains.add(item.trim().toLowerCase().replace(/^www\./, ''));
						}
					}
				}
			}
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				if (key && key.startsWith('guild_vanity_url_')) {
					const val = localStorage.getItem(key);
					if (val && typeof val === 'string' && val.trim()) {
						this.domains.add(val.trim().toLowerCase().replace(/^www\./, ''));
					}
				}
			}
		} catch {
			// ignore storage errors
		}
	}

	private saveToStorage(): void {
		if (typeof window === 'undefined' || !window.localStorage) return;
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.domains)));
		} catch {
			// ignore storage errors
		}
	}

	addDomain(domain: string): void {
		if (!domain) return;
		const clean = domain.trim().toLowerCase().replace(/^www\./, '');
		if (!this.domains.has(clean)) {
			this.domains.add(clean);
			this.domainVersion++;
			this.saveToStorage();
		}
	}

	removeDomain(domain: string): void {
		if (!domain) return;
		const clean = domain.trim().toLowerCase().replace(/^www\./, '');
		if (this.domains.has(clean)) {
			this.domains.delete(clean);
			this.domainVersion++;
			this.saveToStorage();
		}
	}

	isCustomDomain(hostOrUrl: string): boolean {
		if (!hostOrUrl) return false;
		let clean = hostOrUrl.trim().toLowerCase();
		if (clean.includes('/')) {
			try {
				const parsed = new URL(clean.startsWith('http') ? clean : `https://${clean}`);
				clean = parsed.hostname;
			} catch {
				clean = clean.split('/')[0];
			}
		}
		clean = clean.replace(/^www\./, '');
		return this.domains.has(clean);
	}

	async fetchDomains(): Promise<void> {
		if (this.isFetching || this.hasFetched) return;
		this.isFetching = true;
		try {
			const target = currentInstanceTarget();
			const response = await fetch(`${target.apiEndpoint}/v1/invites/custom-domains`);
			if (response.ok) {
				const body = (await response.json()) as {domains?: Array<string>};
				if (Array.isArray(body?.domains)) {
					runInAction(() => {
						for (const d of body.domains!) {
							if (d && typeof d === 'string') {
								this.domains.add(d.trim().toLowerCase().replace(/^www\./, ''));
							}
						}
						this.domainVersion++;
						this.hasFetched = true;
					});
					this.saveToStorage();
				}
			}
		} catch {
			// ignore network errors
		} finally {
			this.isFetching = false;
		}
	}

	bootstrap(): void {
		if (typeof window !== 'undefined' && window.location?.hostname) {
			const host = window.location.hostname.toLowerCase();
			if (
				host !== 'localhost' &&
				host !== '127.0.0.1' &&
				host !== 'nexoriadevelopment.com' &&
				!host.endsWith('.nexoriadevelopment.com') &&
				!host.endsWith('.fluxer.app') &&
				!host.endsWith('.fluxer.com')
			) {
				this.addDomain(host);
			}
		}
		void this.fetchDomains();
	}
}

export default new CustomDomainStore();
